import { t, Trans } from "@lingui/macro";
import { Check, CreditCard, Gear, Globe, MagnifyingGlass, Question, SignOut, User } from "@phosphor-icons/react";
import { useBreakpoint } from "@stylingcv/hooks";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  KeyboardShortcut,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@stylingcv/ui";
import { languages } from "@stylingcv/utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader } from "@/client/components/ui/loader";
import { changeLanguage } from "@/client/providers/locale";
import { useUser } from "@/client/services/user/user";
import { getCurrentUrlLocale } from "@/client/utils/url-locales";

import { useLogout } from "../services/auth";
import { useAuthStore } from "../stores/auth";

type Props = {
  children: React.ReactNode;
};

export const UserOptions = ({ children }: Props) => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const { subscriptionTier } = useAuthStore();
  const { loading, user } = useUser();
  const { isMobile } = useBreakpoint();
  const [open, setOpen] = useState(false);
  const [langSearch, setLangSearch] = useState("");
  const [mobileLangOpen, setMobileLangOpen] = useState(false);

  const currentLocale = getCurrentUrlLocale();

  const filteredLangs = langSearch.trim()
    ? languages.filter((l) =>
        l.name.toLowerCase().includes(langSearch.toLowerCase()) ||
        l.locale.toLowerCase().includes(langSearch.toLowerCase()),
      )
    : languages;

  const currentLangName =
    languages.find(
      (l) =>
        l.locale.toLowerCase() === currentLocale.toLowerCase() ||
        l.id.toLowerCase() === currentLocale.toLowerCase(),
    )?.name ?? "English";

  // Subscription tier badge styling based on plan
  const tierBadgeStyle = () => {
    switch (subscriptionTier) {
      case "BASIC": {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        return "bg-amber-100 hover:bg-amber-200 text-amber-800";
      }
      case "PROFESSIONAL": {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        return "bg-purple-100 hover:bg-purple-200 text-purple-800";
      }
      case "ENTERPRISE": {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        return "bg-emerald-100 hover:bg-emerald-200 text-emerald-800";
      }
      default: {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        return "bg-blue-100 hover:bg-blue-200 text-blue-800";
      }
    }
  };

  // Get plan name based on subscription tier
  const getPlanName = () => {
    switch (subscriptionTier) {
      case "FREE": {
        return t`Free Plan`;
      }
      case "BASIC": {
        return t`Basic Plan`;
      }
      case "PROFESSIONAL": {
        return t`Pro Plan`;
      }
      case "ENTERPRISE": {
        return t`Enterprise Plan`;
      }
      default: {
        return t`Free Plan`;
      }
    }
  };

  if (loading) {
    return <Loader className="size-4 animate-spin text-primary" />;
  }

  // Mobile version using Sheet
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{children}</SheetTrigger>
        <SheetContent side="bottom" className="mobile-user-sheet max-h-[80vh] rounded-t-xl overflow-y-auto">
          <SheetHeader className="pb-4 text-center">
            <SheetTitle className="flex items-center justify-center gap-2">
              <User size={20} />
              <Trans>Account Menu</Trans>
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-4 px-4 pb-6">
            {/* User Email */}
            {user?.email && (
              <div className="text-center">
                <p className="truncate px-2 text-sm font-medium text-muted-foreground">
                  {user.email}
                </p>
              </div>
            )}

            {/* Subscription Badge */}
            <div className="text-center">
              <Badge className={tierBadgeStyle()}>{getPlanName()}</Badge>
            </div>

            {/* Menu Items */}
            <div className="space-y-3">
              <Button
                variant="outline"
                className="mobile-menu-item h-12 w-full justify-start gap-3"
                onClick={() => {
                  navigate("/dashboard/profile");
                  setOpen(false);
                }}
              >
                <User size={20} />
                {t`My Profile`}
              </Button>

              <Button
                variant="outline"
                className="mobile-menu-item h-12 w-full justify-start gap-3"
                onClick={() => {
                  navigate("/dashboard/transaction-history");
                  setOpen(false);
                }}
              >
                <CreditCard size={20} />
                {t`Transaction History`}
              </Button>

              <Button
                variant="outline"
                className="mobile-menu-item h-12 w-full justify-start gap-3"
                onClick={() => {
                  navigate("/dashboard/settings");
                  setOpen(false);
                }}
              >
                <Gear size={20} />
                {t`Settings`}
              </Button>

              <Button
                variant="outline"
                className="mobile-menu-item h-12 w-full justify-start gap-3"
                onClick={() => {
                  navigate("/dashboard/help");
                  setOpen(false);
                }}
              >
                <Question size={20} />
                {t`Help & Support`}
              </Button>

              {/* Language section */}
              <div className="rounded-xl border border-border overflow-hidden">
                <button
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/50"
                  onClick={() => setMobileLangOpen((v) => !v)}
                >
                  <span className="flex items-center gap-3">
                    <Globe size={20} />
                    {t`Language`}
                  </span>
                  <span className="text-xs text-muted-foreground">{currentLangName}</span>
                </button>

                {mobileLangOpen && (
                  <div className="border-t border-border">
                    {/* Search */}
                    <div className="relative p-2">
                      <MagnifyingGlass
                        size={13}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                      />
                      <input
                        value={langSearch}
                        onChange={(e) => setLangSearch(e.target.value)}
                        placeholder={t`Search languages…`}
                        className="w-full h-8 rounded-lg border border-border bg-muted/30 pl-7 pr-3 text-xs outline-none focus:border-[#268599] transition-colors"
                      />
                    </div>
                    {/* List */}
                    <div className="max-h-52 overflow-y-auto">
                      {filteredLangs.map((lang) => {
                        const isActive =
                          lang.locale.toLowerCase() === currentLocale.toLowerCase() ||
                          lang.id.toLowerCase() === currentLocale.toLowerCase();
                        return (
                          <button
                            key={lang.id}
                            className="flex w-full items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-muted/50"
                            onClick={() => {
                              void changeLanguage(lang.locale);
                              setOpen(false);
                            }}
                          >
                            <span className={isActive ? "font-semibold text-[#268599]" : ""}>{lang.name}</span>
                            {isActive && <Check size={14} className="text-[#268599] shrink-0" />}
                          </button>
                        );
                      })}
                      {filteredLangs.length === 0 && (
                        <p className="px-4 py-3 text-xs text-muted-foreground">{t`No languages match`}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Button
                variant="destructive"
                className="mobile-menu-item h-12 w-full justify-start gap-3"
                onClick={() => {
                  logout();
                  setOpen(false);
                }}
              >
                <SignOut size={20} />
                {t`Logout`}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop version using DropdownMenu
  return (
    <DropdownMenu onOpenChange={(v) => { if (!v) setLangSearch(""); }}>
      <DropdownMenuTrigger asChild className="focus-visible:outline-none">{children}</DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        align="start"
        className="w-56 bg-[#112638] border border-white/10 text-white rounded-xl shadow-[0_15px_40px_-10px_rgba(0,0,0,0.5)] p-2 mb-2"
      >
        <div className="px-2 py-2 mb-1">
          <Badge className="bg-[#268599]/20 text-[#268599] hover:bg-[#268599]/30 border-none font-bold tracking-wider uppercase text-xs">
            {getPlanName()}
          </Badge>
        </div>

        <DropdownMenuSeparator className="bg-white/10" />

        <DropdownMenuItem
          className="hover:bg-white/5 focus:bg-white/5 focus:text-white cursor-pointer rounded-lg py-2.5 text-[13px] font-medium gap-3 text-gray-300 transition-colors mt-1"
          onClick={() => { void navigate("/dashboard/profile"); }}
        >
          <User size={18} weight="duotone" className="text-[#268599]" />
          {t`My Profile`}
        </DropdownMenuItem>

        <DropdownMenuItem
          className="hover:bg-white/5 focus:bg-white/5 focus:text-white cursor-pointer rounded-lg py-2.5 text-[13px] font-medium gap-3 text-gray-300 transition-colors"
          onClick={() => { void navigate("/dashboard/transaction-history"); }}
        >
          <CreditCard size={18} weight="duotone" className="text-[#268599]" />
          {t`Transaction History`}
        </DropdownMenuItem>

        <DropdownMenuItem
          className="hover:bg-white/5 focus:bg-white/5 focus:text-white cursor-pointer rounded-lg py-2.5 text-[13px] font-medium gap-3 text-gray-300 transition-colors"
          onClick={() => { void navigate("/dashboard/settings"); }}
        >
          <Gear size={18} weight="duotone" className="text-[#268599]" />
          {t`Settings`}
          {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
          <KeyboardShortcut className="opacity-50 ms-auto text-xs">⇧S</KeyboardShortcut>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="hover:bg-white/5 focus:bg-white/5 focus:text-white cursor-pointer rounded-lg py-2.5 text-[13px] font-medium gap-3 text-gray-300 transition-colors"
          onClick={() => { void navigate("/dashboard/help"); }}
        >
          <Question size={18} weight="duotone" className="text-[#268599]" />
          {t`Help & Support`}
        </DropdownMenuItem>

        {/* Language sub-menu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="hover:bg-white/5 focus:bg-white/5 data-[state=open]:bg-white/5 cursor-pointer rounded-lg py-2.5 text-[13px] font-medium gap-3 text-gray-300 transition-colors w-full flex items-center px-2">
            <Globe size={18} weight="duotone" className="text-[#268599] shrink-0" />
            <span className="flex-1">{t`Language`}</span>
            <span className="text-[11px] text-white/30 truncate max-w-[70px] text-right">{currentLangName}</span>
          </DropdownMenuSubTrigger>

          <DropdownMenuSubContent
            className="w-56 bg-[#0f2132] border border-white/10 rounded-xl shadow-[0_15px_40px_-10px_rgba(0,0,0,0.6)] p-0 overflow-hidden"
            sideOffset={8}
          >
            {/* Search input — stopPropagation prevents Radix from capturing keypresses */}
            <div className="relative p-2 border-b border-white/10">
              <MagnifyingGlass
                size={13}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"
              />
              <input
                value={langSearch}
                onChange={(e) => setLangSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={t`Search languages…`}
                className="w-full h-7 rounded-md bg-white/5 border border-white/10 pl-7 pr-2 text-xs text-white placeholder:text-white/25 outline-none focus:border-[#268599]/60 transition-colors"
              />
            </div>

            {/* Scrollable language list */}
            <div className="max-h-64 overflow-y-auto py-1">
              {filteredLangs.map((lang) => {
                const isActive =
                  lang.locale.toLowerCase() === currentLocale.toLowerCase() ||
                  lang.id.toLowerCase() === currentLocale.toLowerCase();
                return (
                  <DropdownMenuItem
                    key={lang.id}
                    className="cursor-pointer rounded-lg mx-1 py-2 px-3 text-[13px] font-medium gap-3 transition-colors hover:bg-white/5 focus:bg-white/5 focus:text-white"
                    style={{ color: isActive ? "#268599" : "rgb(209 213 219)" }}
                    onClick={() => { void changeLanguage(lang.locale); }}
                  >
                    <span className="flex-1">{lang.name}</span>
                    {isActive && (
                      <Check size={13} className="shrink-0" style={{ color: "#268599" }} />
                    )}
                  </DropdownMenuItem>
                );
              })}

              {filteredLangs.length === 0 && (
                <p className="px-3 py-3 text-xs text-white/30 text-center">{t`No languages match`}</p>
              )}
            </div>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator className="bg-white/10 my-1" />

        <DropdownMenuItem
          className="hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-400 cursor-pointer rounded-lg py-2.5 text-[13px] font-medium gap-3 text-red-400 transition-colors"
          onClick={() => logout()}
        >
          <SignOut size={18} weight="duotone" />
          {t`Logout`}
          {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
          <KeyboardShortcut className="opacity-50 ms-auto text-xs">⇧Q</KeyboardShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
